import type { Messages } from '@amumin/i18n';

import { isValidDateKey, isValidHhmm } from './dates.js';

// Formuliervalidatie van de ouderapp: pure functies, getest met vitest. De server (Zod in
// packages/domain) blijft de bron van waarheid; dit zijn alleen de directe meldingen in de UI.
// Elke functie geeft per veld een sleutel in `Messages['web']` terug (alleen string-sleutels).

type WebMessages = Messages['web'];
/** Sleutels van `web` met een tekstwaarde (geen meervoudsobjecten of geneste groepen). */
export type WebTextKey = {
  [K in keyof WebMessages]: WebMessages[K] extends string ? K : never;
}[keyof WebMessages];

export type FieldErrors<F extends string> = Partial<Record<F, WebTextKey>>;

export function hasErrors(errors: Record<string, unknown>): boolean {
  return Object.keys(errors).length > 0;
}

export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 256;

/** Zelfde eis als `Email` in packages/domain: getrimd, lowercase, met @ en domein. */
export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function isValidEmail(value: string): boolean {
  const v = normalizeEmail(value);
  return v.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export function validatePassword(password: string): WebTextKey | undefined {
  if (password.length < PASSWORD_MIN) return 'passwordTooShort';
  if (password.length > PASSWORD_MAX) return 'passwordTooLong';
  return undefined;
}

export function validateRegister(input: {
  email: string;
  password: string;
  repeat: string;
}): FieldErrors<'email' | 'password' | 'repeat'> {
  const errors: FieldErrors<'email' | 'password' | 'repeat'> = {};
  if (!isValidEmail(input.email)) errors.email = 'emailInvalid';
  const pw = validatePassword(input.password);
  if (pw) errors.password = pw;
  else if (input.repeat !== input.password) errors.repeat = 'passwordMismatch';
  return errors;
}

export function validateLogin(input: {
  email: string;
  password: string;
}): FieldErrors<'email' | 'password'> {
  const errors: FieldErrors<'email' | 'password'> = {};
  if (!isValidEmail(input.email)) errors.email = 'emailInvalid';
  if (input.password.length === 0 || input.password.length > PASSWORD_MAX)
    errors.password = 'invalidCredentials';
  return errors;
}

/** 6-cijferige e-mailcode; spaties en streepjes ("123 456") worden genegeerd. */
export function normalizeCode(value: string): string {
  return value.replace(/[^0-9]/g, '');
}

export function validateCode(value: string): WebTextKey | undefined {
  return normalizeCode(value).length === 6 ? undefined : 'codeInvalid';
}

export const USER_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** Koppelcode: hoofdletters, zonder streepje/spatie; null als de vorm niet klopt. */
export function normalizeUserCode(value: string): string | null {
  const code = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (code.length !== 8) return null;
  if (/^[0-9]{8}$/.test(code)) return code;
  for (const ch of code) if (!USER_CODE_ALPHABET.includes(ch)) return null;
  return code;
}

/** `ABCD2345` → `ABCD-2345` (weergave op de tablet en in de URL). */
export function formatUserCode(code: string): string {
  const c = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4, 8)}` : c;
}

export const TITLE_MAX = 120;
export const NAME_MAX = 40;
export const DURATION_MIN = 5;
export const DURATION_MAX = 1440;

export interface EventFormValues {
  title: string;
  date: string;
  all_day: boolean;
  start_hhmm: string;
  duration_min: string;
  member_ids: string[] | 'everyone';
}

export function validateEventForm(
  v: EventFormValues,
): FieldErrors<'title' | 'date' | 'start_hhmm' | 'duration_min' | 'member_ids'> {
  const errors: FieldErrors<'title' | 'date' | 'start_hhmm' | 'duration_min' | 'member_ids'> = {};
  const title = v.title.trim();
  if (title.length === 0) errors.title = 'titleRequired';
  else if (title.length > TITLE_MAX) errors.title = 'titleTooLong';
  if (!isValidDateKey(v.date)) errors.date = 'dateInvalid';
  if (!v.all_day) {
    if (!isValidHhmm(v.start_hhmm)) errors.start_hhmm = 'startInvalid';
    const duration = Number(v.duration_min);
    if (!Number.isInteger(duration) || duration < DURATION_MIN || duration > DURATION_MAX)
      errors.duration_min = 'durationInvalid';
  }
  if (v.member_ids !== 'everyone' && v.member_ids.length === 0)
    errors.member_ids = 'memberRequired';
  return errors;
}

export interface TaskFormValues {
  title: string;
  date: string;
  member_ids: string[];
}

export function validateTaskForm(v: TaskFormValues): FieldErrors<'title' | 'date' | 'member_ids'> {
  const errors: FieldErrors<'title' | 'date' | 'member_ids'> = {};
  const title = v.title.trim();
  if (title.length === 0) errors.title = 'titleRequired';
  else if (title.length > TITLE_MAX) errors.title = 'titleTooLong';
  if (!isValidDateKey(v.date)) errors.date = 'dateInvalid';
  if (v.member_ids.length === 0) errors.member_ids = 'memberRequired';
  return errors;
}

export function validateName(name: string): WebTextKey | undefined {
  const n = name.trim();
  if (n.length === 0) return 'nameRequired';
  if (n.length > NAME_MAX) return 'nameTooLong';
  return undefined;
}

export function validateHouseholdName(name: string): WebTextKey | undefined {
  const n = name.trim();
  if (n.length === 0) return 'householdNameRequired';
  if (n.length > 80) return 'nameTooLong';
  return undefined;
}

// --- Vertrouwde netwerken (ONTWERP.md §11.1.3/§11.1.4) ---------------------------------------

export function parseIpv4(value: string): [number, number, number, number] | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(value.trim());
  if (!m) return null;
  const octets = [m[1], m[2], m[3], m[4]].map(Number) as [number, number, number, number];
  return octets.every((o) => o >= 0 && o <= 255) ? octets : null;
}

/** CGNAT 100.64.0.0/10 (RFC 6598): kan nooit vertrouwd worden. */
export function isCgnat(ip: string): boolean {
  const o = parseIpv4(ip);
  return o !== null && o[0] === 100 && o[1] >= 64 && o[1] <= 127;
}

export interface NetworkFormValues {
  label: string;
  kind: 'cidr' | 'ip' | 'server_wan' | 'ddns_hostname';
  value: string;
}

/** Client-side voorcontrole; de server beslist (loopback, plan-limieten, dubbele rijen). */
export function validateNetworkForm(v: NetworkFormValues): FieldErrors<'label' | 'value'> {
  const errors: FieldErrors<'label' | 'value'> = {};
  const label = v.label.trim();
  if (label.length === 0 || label.length > 40) errors.label = 'labelRequired';
  const value = v.value.trim();
  if (v.kind === 'server_wan') return errors;
  if (value.includes(':')) {
    errors.value = 'valueIpv6';
    return errors;
  }
  if (v.kind === 'ip') {
    if (!parseIpv4(value)) errors.value = 'valueInvalid';
    else if (isCgnat(value)) errors.value = 'valueCgnat';
  } else if (v.kind === 'cidr') {
    const m = /^([0-9.]+)\/(\d{1,2})$/.exec(value);
    const prefix = m ? Number(m[2]) : NaN;
    // prefix_len v4 24–32; nooit breder (een /16 omvat op een Fritz!Box ook het gastnetwerk).
    if (!m || !parseIpv4(m[1] ?? '') || prefix < 24 || prefix > 32) errors.value = 'valueInvalid';
    else if (isCgnat(m[1] ?? '')) errors.value = 'valueCgnat';
  } else if (v.kind === 'ddns_hostname') {
    if (
      !/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(
        value.toLowerCase(),
      )
    )
      errors.value = 'valueInvalid';
  }
  return errors;
}

/** Gemaskeerde prefix van de API (`84.12.7.•`) → `/24`-CIDR voor "Dit netwerk vertrouwen". */
export function prefixToCidr(prefix: string): string | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(?:•|x|\*|\d{1,3})$/i.exec(prefix.trim());
  if (!m) return null;
  const base = `${m[1]}.${m[2]}.${m[3]}.0`;
  return parseIpv4(base) ? `${base}/24` : null;
}

/** Privé-bereik (RFC 1918) of loopback: dan is de `/24` van het LAN de logische suggestie. */
export function isPrivateIpv4(ip: string): boolean {
  const o = parseIpv4(ip);
  if (!o) return false;
  return (
    o[0] === 10 ||
    (o[0] === 172 && o[1] >= 16 && o[1] <= 31) ||
    (o[0] === 192 && o[1] === 168) ||
    o[0] === 127
  );
}
